/**
 * Which session types may show the LIVE "Looks like you've left — record your
 * departure?" prompt (pure; shared by the frontend store and tests).
 *
 * Owner rule 2026-10-19 (build 127 field test): Support Work and
 * community-access style sessions legitimately leave the address during the
 * visit (a drive to a venue, an outing with the member), so the live prompt
 * is OFF entirely for them. Everything else — and any session whose type we
 * cannot read — keeps the prompt (unknown ⇒ allow). The check-out departure
 * SUGGESTION is unaffected: it is only a sheet at check-out.
 *
 * Source: `Session.serviceType` (AHP `service_type`). TEST data on
 * 2026-10-19 contained exactly: "Support Work", "Physiotherapy",
 * "Speech Pathology".
 */

/**
 * DENY list — a service type is denied when its normalised text (lowercase,
 * punctuation → single spaces) contains any of these phrases ON WORD
 * BOUNDARIES (so "Rerouting" never matches "outing" and "Psychosocial Support"
 * never matches "social support"). Deliberately phrase-based, not a bare
 * "community": "Community Nursing" is a home visit and keeps the prompt.
 */
export const DEPARTURE_PROMPT_DENY_PHRASES: readonly string[] = [
  'support work',
  'support worker',
  'community access',
  'community participation',
  'community outing',
  'social and community',
  'social support',
  'social outing',
  'social participation',
  'group outing',
  'outing',
  'outings',
  'recreation',
  'recreational',
  'recreation activities',
  'recreation activity',
  'social supports',
  'community outings',
]

/** Whole-value DENY entries (the entire normalised service type equals one). */
export const DEPARTURE_PROMPT_DENY_EXACT: readonly string[] = ['community', 'social', 'social activity']

/**
 * ALLOW list — documentation + test fixtures only. Anything not denied is
 * allowed; these are the types known to want the live prompt.
 */
export const DEPARTURE_PROMPT_KNOWN_ALLOWED: readonly string[] = [
  'physiotherapy',
  'speech pathology',
  'occupational therapy',
  'psychology',
  'dietetics',
  'exercise physiology',
]

function normalise(value: unknown): string {
  if (typeof value !== 'string') return ''
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * May this session show the live geofence-exit departure prompt?
 * False for Support Work / community-access style service types; true for
 * everything else, including a missing or unreadable type.
 */
export function allowsDepartureAutoPrompt(
  session: { serviceType?: string | null } | null | undefined,
): boolean {
  const type = normalise(session?.serviceType)
  if (type === '') return true
  if (DEPARTURE_PROMPT_DENY_EXACT.includes(type)) return false
  // Pad with spaces so a phrase only matches whole words of the normalised string.
  const padded = ` ${type} `
  return !DEPARTURE_PROMPT_DENY_PHRASES.some((phrase) => padded.includes(` ${phrase} `))
}
